import { ApprovalMode } from "../approvalModes";
import type { ClientToolDeps, ClientToolExecutionResult } from "../ClientTools";
import { createClientTool, toolError } from "../ClientTools";

import { AddToPanelConfirmation } from "./confirmations";
import {
  fetchEntries,
  isStringArray,
  MAX_MESSAGE_PATHS_PER_CALL,
  runPanelMutation,
} from "./workspaceActions";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface AddToPanelInput {
  panel_id: string;
  message_path_ids: string[];
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

async function execute(
  input: AddToPanelInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  if (!deps?.topicService) {
    return toolError("Topic service is not available");
  }
  if (!input.panel_id) {
    return toolError("panel_id is required");
  }
  if (!input.message_path_ids || input.message_path_ids.length === 0) {
    return toolError("message_path_ids is required");
  }
  if (!isStringArray(input.message_path_ids)) {
    return toolError(
      "message_path_ids must be an array of message path id strings",
    );
  }
  if (input.message_path_ids.length > MAX_MESSAGE_PATHS_PER_CALL) {
    return toolError(
      `At most ${MAX_MESSAGE_PATHS_PER_CALL} message paths may be added in one call.`,
    );
  }

  if (!deps.isOnVisualizerPage?.() || !deps.publishWorkspaceAction) {
    return toolError(
      "The visualizer is not open, so there are no panels to modify. " +
        "Use client_visualize_open_panel to open data first.",
    );
  }
  try {
    const { entries, invalid } = await fetchEntries(
      deps.topicService,
      input.message_path_ids,
    );
    if (invalid.length > 0) {
      return toolError("Some message paths could not be loaded.", {
        invalid_paths: invalid,
      });
    }

    return runPanelMutation(
      deps.publishWorkspaceAction,
      "add_to_panel",
      (onResult) => ({
        type: "add_to_panel",
        payload: {
          panelId: input.panel_id,
          entries,
          onResult,
        },
      }),
    );
  } catch (err) {
    return toolError(err instanceof Error ? err.message : String(err));
  }
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<AddToPanelInput>({
  name: "client_visualize_add_to_panel",
  description: `Add one or more message paths to an existing visualizer panel — e.g. add pitch and roll as extra series on a plot that already shows yaw. A path holding an array of numbers can join a plot too, charted as one series drawing a line per index. The panel is referenced by its id from the visualizer state context or from a previous open-panel result. Only works while the user is on the visualizer page. Returns the panel_type it added to. On failure nothing is added and the error lists a per-path reason (wrong data type, already in the panel, etc.) — adjust the request and retry.`,
  input_schema: {
    type: "object",
    properties: {
      panel_id: {
        type: "string",
        description:
          "Id of the target panel. Panel ids are visible in the visualizer state context and returned by client_visualize_open_panel.",
      },
      message_path_ids: {
        type: "array",
        items: { type: "string" },
        minItems: 1,
        maxItems: MAX_MESSAGE_PATHS_PER_CALL,
        description:
          "Message path IDs (e.g., ['mp_abc123']) to add. Each must be compatible with the panel's type and not already on the panel.",
      },
    },
    required: ["panel_id", "message_path_ids"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Add to Panel",
  displayDescription: "Adds data to an existing visualizer panel.",
  ConfirmationContent: AddToPanelConfirmation,
});
